<template>
  <div class="flex-1 overflow-auto p-6">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="mb-6 flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Histórico de Backups
          </h1>
          <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Todos os backups realizados — automáticos e manuais.
          </p>
        </div>
        <router-link
          :to="{ name: 'backup_settings', params: $route.params }"
          class="text-sm text-blue-600 hover:underline"
        >
          ← Configurações
        </router-link>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex justify-center py-12">
        <span class="text-slate-400">Carregando...</span>
      </div>

      <!-- Empty -->
      <div
        v-else-if="!backups.length"
        class="text-center py-16 text-slate-400"
      >
        <p class="text-4xl mb-3">📦</p>
        <p class="font-medium">Nenhum backup encontrado.</p>
        <p class="text-sm mt-1">
          Ative o backup em conversas e aguarde o agendamento diário, ou faça um backup manual.
        </p>
      </div>

      <!-- Table -->
      <div v-else class="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
            <tr>
              <th class="px-4 py-3 text-left font-medium">Conversa</th>
              <th class="px-4 py-3 text-left font-medium">Tipo</th>
              <th class="px-4 py-3 text-left font-medium">Status</th>
              <th class="px-4 py-3 text-left font-medium">Data</th>
              <th class="px-4 py-3 text-left font-medium">Arquivos</th>
              <th class="px-4 py-3 text-right font-medium">Ações</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-700">
            <tr
              v-for="backup in backups"
              :key="backup.id"
              class="hover:bg-slate-50 dark:hover:bg-slate-800/50"
            >
              <td class="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                #{{ backup.conversation_display_id }}
              </td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                  :class="
                    backup.backup_type === 'manual'
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  "
                >
                  {{ backup.backup_type === 'manual' ? '🖐 Manual' : '⏰ Automático' }}
                </span>
              </td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                  :class="statusClass(backup.status)"
                >
                  {{ statusLabel(backup.status) }}
                </span>
              </td>
              <td class="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                {{ formatDate(backup.created_at) }}
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap items-center gap-2">
                  <!-- ZIP download -->
                  <a
                    v-if="backup.zip_url"
                    :href="backup.zip_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-600 text-white hover:bg-blue-700"
                    title="Baixar ZIP completo (PDF + JSON + anexos)"
                  >
                    ⬇ ZIP
                  </a>
                  <!-- PDF download -->
                  <a
                    v-if="backup.pdf_url"
                    :href="backup.pdf_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs text-blue-600 hover:underline"
                  >PDF</a>
                  <!-- JSON download -->
                  <a
                    v-if="backup.json_url"
                    :href="backup.json_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs text-blue-600 hover:underline"
                  >JSON</a>
                  <!-- Attachments count -->
                  <span
                    v-if="backup.attachments_manifest && backup.attachments_manifest.length"
                    class="text-xs text-slate-500"
                  >
                    +{{ backup.attachments_manifest.length }} anexo(s)
                  </span>
                </div>
                <p v-if="backup.error_message" class="text-xs text-red-500 mt-1">
                  {{ backup.error_message }}
                </p>
              </td>
              <td class="px-4 py-3 text-right">
                <div class="flex items-center justify-end gap-3">
                  <!-- View as chat -->
                  <router-link
                    v-if="backup.status === 'completed'"
                    :to="{
                      name: 'backup_viewer',
                      params: { accountId: $route.params.accountId, backupId: backup.id }
                    }"
                    class="text-xs text-blue-600 hover:underline"
                  >
                    Visualizar
                  </router-link>
                  <!-- Delete -->
                  <button
                    class="text-xs text-red-500 hover:underline"
                    @click="confirmDelete(backup)"
                  >
                    Excluir
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div
          v-if="meta.total_pages > 1"
          class="flex items-center justify-between px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800"
        >
          <p class="text-xs text-slate-500">
            {{ meta.total }} backups · página {{ meta.current_page }} de {{ meta.total_pages }}
          </p>
          <div class="flex gap-2">
            <button
              :disabled="meta.current_page <= 1"
              class="px-3 py-1 text-xs rounded border border-slate-300 dark:border-slate-600 disabled:opacity-40"
              @click="loadPage(meta.current_page - 1)"
            >
              Anterior
            </button>
            <button
              :disabled="meta.current_page >= meta.total_pages"
              class="px-3 py-1 text-xs rounded border border-slate-300 dark:border-slate-600 disabled:opacity-40"
              @click="loadPage(meta.current_page + 1)"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>

      <!-- Delete modal -->
      <div
        v-if="deleteTarget"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      >
        <div class="bg-white dark:bg-slate-900 rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
          <h3 class="font-semibold text-slate-900 dark:text-slate-100 mb-2">Excluir backup?</h3>
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Backup da conversa #{{ deleteTarget.conversation_display_id }} de
            {{ formatDate(deleteTarget.created_at) }}.
          </p>
          <label class="flex items-center gap-2 mb-4 cursor-pointer">
            <input v-model="deleteFromR2" type="checkbox" class="rounded" />
            <span class="text-sm text-slate-700 dark:text-slate-300">
              Também excluir arquivos do R2 (inclui ZIP)
            </span>
          </label>
          <div class="flex gap-3 justify-end">
            <button
              class="px-4 py-2 text-sm rounded border border-slate-300 dark:border-slate-600"
              @click="deleteTarget = null"
            >
              Cancelar
            </button>
            <button
              :disabled="deleting"
              class="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              @click="deleteBackup"
            >
              {{ deleting ? 'Excluindo...' : 'Excluir' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import backupAPI from '../../../../api/backup';

export default {
  name: 'BackupHistoryPage',
  data() {
    return {
      loading: true,
      backups: [],
      meta: { total: 0, current_page: 1, total_pages: 1 },
      deleteTarget: null,
      deleteFromR2: false,
      deleting: false,
    };
  },
  async created() {
    await this.loadPage(1);
  },
  methods: {
    async loadPage(page) {
      this.loading = true;
      try {
        const { data } = await backupAPI.getAllBackups(page);
        this.backups = data.backups;
        this.meta = data.meta;
      } catch (e) {
        this.backups = [];
      } finally {
        this.loading = false;
      }
    },
    confirmDelete(backup) {
      this.deleteTarget = backup;
      this.deleteFromR2 = false;
    },
    async deleteBackup() {
      this.deleting = true;
      try {
        await backupAPI.deleteBackup(this.deleteTarget.id, this.deleteFromR2);
        this.backups = this.backups.filter(b => b.id !== this.deleteTarget.id);
        this.deleteTarget = null;
      } catch (e) {
        alert('Erro ao excluir backup.');
      } finally {
        this.deleting = false;
      }
    },
    statusLabel(status) {
      return { pending: '⏳ Pendente', processing: '🔄 Processando', completed: '✅ Concluído', failed: '❌ Falhou' }[status] || status;
    },
    statusClass(status) {
      return {
        pending:    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
        processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        completed:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
        failed:     'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      }[status] || 'bg-slate-100 text-slate-700';
    },
    formatDate(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    },
  },
};
</script>
